// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CheckCircle as CheckCircleIcon,
  CheckRounded,
  ContentCopyRounded,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@/constants/hardfin-standard-icons';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { ShipmentDataUploadResult } from '@/shipment/api/upload-shipment-data';
import type { ExpandedShipmentsState } from '@/shipment/components/shipment-data-upload/types';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  createIdentifierSuffix,
  formatFailedRowsForCopy,
} from '@/utilities/upload-helpers';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FixedSizeList as VirtualList } from 'react-window';

const ITEM_HEIGHT = 48;
const FAILED_ROW_HEIGHT = 80;
const SHIPPING_COST_FIELD = 'shipping cost';
const COPY_FEEDBACK_RESET_MS = 2000;

interface ResultsProps {
  uploadResult: ShipmentDataUploadResult | null;
  expandedShipments: ExpandedShipmentsState;
  onToggleExpanded: (section: keyof ExpandedShipmentsState) => void;
  onBack: () => void;
  onStartNew: () => void;
  onClose: () => void;
}

interface RowFailure {
  rowNumber: number;
  shipmentNumber: string;
  errors: string[];
}

type ShipmentItem = ShipmentDataUploadResult['items'][number];

interface ShipmentRowData {
  shipments: ShipmentItem[];
  uploadSucceeded: boolean;
}

function formatShippingCostChange(
  shipment: ShipmentItem,
  uploadSucceeded: boolean
) {
  if (!(SHIPPING_COST_FIELD in shipment.fieldsUpdated)) {
    return 'Shipping cost not included';
  }
  if (!shipment.fieldsUpdated[SHIPPING_COST_FIELD]) {
    return 'Shipping cost unchanged';
  }
  if (shipment.shippingCost === null) {
    return uploadSucceeded
      ? 'Shipping cost cleared'
      : 'Shipping cost would be cleared';
  }
  return uploadSucceeded
    ? `Shipping cost updated to ${formatCurrencyValue(shipment.shippingCost)}`
    : `Shipping cost would update to ${formatCurrencyValue(shipment.shippingCost)}`;
}

interface ShipmentRowProps {
  index: number;
  style: React.CSSProperties;
  data: ShipmentRowData;
}

function ShipmentRow(props: ShipmentRowProps) {
  const shipment = props.data.shipments[props.index];

  return (
    <div style={props.style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={shipment.shipmentNumber}
          secondary={formatShippingCostChange(
            shipment,
            props.data.uploadSucceeded
          )}
        />
      </ListItem>
    </div>
  );
}

interface FailedRowItemProps {
  index: number;
  style: React.CSSProperties;
  data: RowFailure[];
}

function FailedRowItem(props: FailedRowItemProps) {
  const row = props.data[props.index];

  return (
    <div style={props.style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={`For file row ${row.rowNumber}${row.shipmentNumber ? ` (shipment "${row.shipmentNumber}")` : ''}:`}
          secondary={
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {row.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          }
          secondaryTypographyProps={{ component: 'div' }}
        />
      </ListItem>
    </div>
  );
}

export function Results(props: ResultsProps) {
  const [isCopied, setIsCopied] = useState(false);

  if (!props.uploadResult) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            No upload results available. Please try again.
          </Typography>
          <Button variant="contained" onClick={props.onStartNew} sx={{ mt: 2 }}>
            Start new upload
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { success, items, structureErrors, rowErrors } = props.uploadResult;
  const updatedShipments = items.filter(
    (item) => !item.errors || item.errors.length === 0
  );
  const failedRows: RowFailure[] = [];
  const hasStructureErrors = structureErrors.length > 0;

  const handleCopyErrors = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const text = formatFailedRowsForCopy(failedRows, (row) =>
        createIdentifierSuffix(row, 'shipmentNumber', 'shipment')
      );
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), COPY_FEEDBACK_RESET_MS);
    } catch (error) {
      console.error('Failed to copy errors:', error);
    }
  };

  rowErrors.forEach((row) => {
    if (row.errors.length > 0) {
      failedRows.push({
        rowNumber: row.index,
        shipmentNumber: '',
        errors: row.errors.map(
          (err) => `Column "${err.fieldName}" has an error: ${err.error}`
        ),
      });
    }
  });

  items.forEach((item) => {
    if (item.errors && item.errors.length > 0) {
      failedRows.push({
        rowNumber: item.index,
        shipmentNumber: item.shipmentNumber,
        errors: item.errors.map(
          (err) => `Column "${err.fieldName}" has an error: ${err.error}`
        ),
      });
    }
  });

  const hasRowErrors = failedRows.length > 0;
  const uploadSucceeded = success && !hasStructureErrors && !hasRowErrors;

  return (
    <Card>
      <CardContent>
        {uploadSucceeded ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>Success</AlertTitle>
              <Typography variant="body2">
                Your shipment data uploaded successfully for{' '}
                {pluralize(updatedShipments.length, {
                  singular: 'shipment',
                  plural: 'shipments',
                })}
              </Typography>
            </Alert>

            {updatedShipments.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('updated')}
                >
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      {`Shipments processed (${formatNumber(updatedShipments.length)})`}
                    </Typography>
                  </Box>
                  {props.expandedShipments.updated ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={props.expandedShipments.updated}>
                  <VirtualList
                    height={Math.min(
                      updatedShipments.length * ITEM_HEIGHT,
                      200
                    )}
                    itemCount={updatedShipments.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={{
                      shipments: updatedShipments,
                      uploadSucceeded,
                    }}
                    width="100%"
                  >
                    {ShipmentRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {hasStructureErrors && (
                <>
                  <AlertTitle>File format invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file has column names that do not match the
                    required shipment data fields
                  </Typography>
                </>
              )}
              {hasRowErrors && (
                <>
                  <AlertTitle>Uploaded data invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file includes shipment data that does not match
                    required fields
                  </Typography>
                </>
              )}
            </Alert>

            {updatedShipments.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('updated')}
                >
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      {`Valid rows not uploaded (${formatNumber(updatedShipments.length)})`}
                    </Typography>
                  </Box>
                  {props.expandedShipments.updated ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={props.expandedShipments.updated}>
                  <VirtualList
                    height={Math.min(
                      updatedShipments.length * ITEM_HEIGHT,
                      200
                    )}
                    itemCount={updatedShipments.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={{
                      shipments: updatedShipments,
                      uploadSucceeded,
                    }}
                    width="100%"
                  >
                    {ShipmentRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}

            {structureErrors.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('structureErrors')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      {`Mismatched columns (${formatNumber(structureErrors.length)})`}
                    </Typography>
                  </Box>
                  {props.expandedShipments.structureErrors ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={props.expandedShipments.structureErrors}>
                  <Box
                    component="ul"
                    sx={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      listStyleType: 'disc',
                    }}
                  >
                    {structureErrors.map((row) => (
                      <li
                        key={`structure-${row.fieldName}`}
                        style={{ paddingBottom: '8px' }}
                      >
                        Column "{row.fieldName}" not found
                      </li>
                    ))}
                  </Box>
                </Collapse>
              </Paper>
            )}

            {hasRowErrors && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('failed')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      {`Invalid rows (${formatNumber(failedRows.length)} ${pluralize(
                        failedRows.length,
                        {
                          includeCount: false,
                          singular: 'shipment',
                          plural: 'shipments',
                        }
                      )})`}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={isCopied ? 'Copied!' : 'Copy all errors'}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={handleCopyErrors}
                        aria-label={isCopied ? 'Copied' : 'Copy all errors'}
                        sx={{
                          color: 'error.contrastText',
                          '&:hover': {
                            bgcolor: 'error.dark',
                          },
                        }}
                      >
                        {isCopied ? (
                          <CheckRounded fontSize="small" />
                        ) : (
                          <ContentCopyRounded fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {props.expandedShipments.failed ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </Box>
                </Box>
                <Collapse in={props.expandedShipments.failed}>
                  <VirtualList
                    height={Math.min(
                      failedRows.length * FAILED_ROW_HEIGHT,
                      300
                    )}
                    itemCount={failedRows.length}
                    itemSize={FAILED_ROW_HEIGHT}
                    itemData={failedRows}
                    width="100%"
                  >
                    {FailedRowItem}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        )}

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button variant="outlined" onClick={props.onBack}>
            Back
          </Button>
          <Box>
            <Button
              variant="outlined"
              onClick={props.onStartNew}
              sx={{ mr: 1 }}
            >
              Start new upload
            </Button>
            <Button variant="contained" onClick={props.onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
